import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, lastValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { GenericResponseDto } from "../models/generic-response.dto";
import { FreightChargesListDto } from "../models/freight-quote/freightChargesListDto";
import { FreightCarriersListDto } from "../models/freight-quote/freightCarriersListDto";
import { FreightShipmentDto } from "../models/freight-quote/freightShipmentDto";
import { FreightShipmentRequestDto } from "../models/freight-quote/freightShipmentRequestDto";
import { FreightClientVendorSearchDto } from "../models/freight-quote/freightClientVendorSearchDto";
import { GetFreightEstimationTooltipManagement } from "../models/freight-quote/freightEstimationTooltipManagement";
import { FreightListingDto } from "../models/freight-quote/freightListingPayload.dto";
import { FreightQuotePdfDto } from "../models/freight-quote/freightQuotePdfDto";

@Injectable({
  providedIn: "root",
})
export class FreightQuoteService {
  private saleOrderManagementBaseURL =
    environment.baseUrl + "/SalesOrderManagement";
  private commonBaseURL = environment.baseUrl + "/Common";
  private freightBaseURL = environment.baseUrl + "/FreightEstimation";
  private melissaBaseURL = environment.baseUrl + "/Melissa";
  constructor(private _http: HttpClient) {}

  getFreightCharges(
    saleOrderId: string
  ): Observable<GenericResponseDto<FreightChargesListDto[]>> {
    return this._http.get<GenericResponseDto<FreightChargesListDto[]>>(
      this.saleOrderManagementBaseURL +
        `/GetFreightChargesDetails?SaleOrderId=${saleOrderId}`
    );
  }

  getFreightChargesData(
    saleOrderId: string
  ): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.saleOrderManagementBaseURL +
        `/GetFreightCharges?SaleOrderId=${saleOrderId}`
    );
  }

  getFreightQuotesList(
    payload: FreightListingDto
  ): Observable<GenericResponseDto<FreightChargesListDto[]>> {
    return this._http.post<GenericResponseDto<FreightChargesListDto[]>>(
      this.freightBaseURL + `/GetQoutesList`,
      payload
    );
  }

  generateQoutePdfById(quoteId: number): Observable<
    GenericResponseDto<{
      pdfBase64: string;
      url: string;
      fileName: string;
    }>
  > {
    return this._http.get<
      GenericResponseDto<{
        pdfBase64: string;
        url: string;
        fileName: string;
      }>
    >(`${this.freightBaseURL}/GenerateQuotePdfById?quoteId=${quoteId}`);
  }

  getFreightEstimationShipments(
    propertyName: string,
    value: string,
    lineItems?: string
  ): Observable<GenericResponseDto<FreightShipmentDto>> {
    return this._http.get<GenericResponseDto<FreightShipmentDto>>(
      this.freightBaseURL +
        `/GetFreightEstimationShipments?propertyName=${propertyName}&value=${value}&lineItems=${lineItems}`
    );
  }

  getFreightCarriersList(): Observable<
    GenericResponseDto<FreightCarriersListDto[]>
  > {
    return this._http.get<GenericResponseDto<FreightCarriersListDto[]>>(
      `${this.freightBaseURL}/GetFreightCarriersList`
    );
  }

  searchClientAndVendor(
    value: string,
    type: string,
    country: string
  ): Observable<GenericResponseDto<FreightClientVendorSearchDto[]>> {
    return this._http.get<GenericResponseDto<FreightClientVendorSearchDto[]>>(
      `${this.freightBaseURL}/FreightEstimationSearchClientAndVendor?type=${type}&value=${value}&country=${country}`
    );
  }

  addFreightEstimation(
    payload: FreightShipmentRequestDto
  ): Observable<GenericResponseDto<FreightShipmentDto>> {
    return this._http.post<GenericResponseDto<FreightShipmentDto>>(
      this.freightBaseURL + "/CreateFreightEstimation",
      payload
    );
  }

  updateFreightEstimation(
    payload: FreightShipmentRequestDto
  ): Observable<GenericResponseDto<FreightShipmentDto>> {
    return this._http.post<GenericResponseDto<FreightShipmentDto>>(
      this.freightBaseURL + "/UpdateFreightEstimation",
      payload
    );
  }

  getFreightQuote(
    payload: FreightShipmentRequestDto
  ): Observable<GenericResponseDto<FreightShipmentDto>> {
    return this._http.post<GenericResponseDto<FreightShipmentDto>>(
      this.freightBaseURL + "/GetFreightQoute",
      payload
    );
  }

  getFreightEstimation(
    quoteId: number
  ): Observable<GenericResponseDto<FreightShipmentDto>> {
    return this._http.get<GenericResponseDto<FreightShipmentDto>>(
      this.freightBaseURL + `/GetFreightEstimation?quoteId=${quoteId}`
    );
  }

  getFreightEstimationTooltipManagement(): Observable<
    GenericResponseDto<GetFreightEstimationTooltipManagement[]>
  > {
    return this._http.get<
      GenericResponseDto<GetFreightEstimationTooltipManagement[]>
    >(`${this.freightBaseURL}/GetFreightEstimationTooltipManagement`);
  }

  generateQoutePdf(
    payload: number
  ): Observable<GenericResponseDto<FreightQuotePdfDto>> {
    return this._http.post<GenericResponseDto<FreightQuotePdfDto>>(
      this.freightBaseURL + "/GenerateQuotePdf",
      payload
    );
  }

  getMelissaDataByPostalCode(street: string, country: string): Promise<[]> {
    return lastValueFrom(
      this._http.get<[]>(
        `${this.melissaBaseURL}/GetMelisaPostalCodeSearch?postalCode=${street}&country=${country}`
      )
    );
  }
}
